import { useState, Fragment } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { IdCard as IdCardIcon } from 'lucide-react';
import { onboardingApi } from '../../api/onboarding';
import { probationApi } from '../../api/probation';
import { employeeApi, idCardApi, employmentHistoryApi } from '../../api/hr';
import { contractApi, contractTemplateApi } from '../../api/contracts';
import { leaveApi } from '../../api/leave';
import { trainingApi } from '../../api/training';
import { performanceApi } from '../../api/performance';
import { ApiError } from '../../api/client';
import { StatusBadge } from '../../components/StatusBadge';
import { ReadOnlyBanner } from '../../components/ReadOnlyBanner';
import { IconActionButton } from '../../components/IconActionButton';
import { Trash2, Check, LogOut, MinusCircle, RotateCcw, Eye } from 'lucide-react';
import { DataTable } from '../../components/DataTable';
import { useToast } from '../../components/Toast';
import { Employee } from '../../types/hr';
import { DocumentsSection } from '../../components/DocumentsSection';
import { employeeDocumentApi } from '../../api/documents';
import { departmentsApi } from '../../api/admin/departments';
import { positionsApi } from '../../api/admin/positions';
import { CommentsThread } from '../../components/CommentsThread';
import { WorkflowTrackingTab } from '../../components/WorkflowTrackingTab';
import { EMPLOYEE_STAGES } from '../../config/workflow-stages';
import { timesheetApi } from '../../api/payroll';
import { staffMovementApi } from '../../api/staff-movement';
import { separationApi } from '../../api/separation';
import { restrictedCaseApi } from '../../api/restricted-cases';
import { usePermission } from '../../auth/AuthContext';
import { identificationApi, qualificationApi, compensationHistoryApi, projectAssignmentApi } from '../../api/staff-master-record';
import { projectsApi } from '../../api/projects';
import { formatDate } from '../../utils/formatDate';
import { EmployeePicker } from '../../components/EmployeePicker';
import { trackingApi } from '../../api/tracking';

const TABS = ['Overview', 'Employment', 'Contracts', 'Personal', 'Projects', 'Onboarding', 'Probation', 'Leave', 'Timesheets', 'Training', 'Performance', 'Documents', 'Movements', 'Separation', 'Restricted', 'Tracking'] as const;
type Tab = typeof TABS[number];

/**
 * Real tabbed workspace, matching the reference design - previously
 * one long scrolling page with Leave/Training/Performance completely
 * absent (confirmed via inspection: the data layer already supported
 * per-employee lookups, they were simply never exposed as endpoints
 * or shown here). Same underlying data and mutations as before, just
 * genuinely reorganised into one connected workspace.
 */
export function StaffDetailPage() {
  const { employeeId } = useParams<{ employeeId: string }>();
  const id = Number(employeeId);
  const { showSuccess, showError } = useToast();
  const queryClient = useQueryClient();
  const [activeTab, setActiveTab] = useState<Tab>('Overview');
  const canDeleteTimesheets = usePermission('timesheets.delete');
  const canManage = usePermission('employees.manage');
  const canViewCompensation = usePermission('compensation.view_history');
  const canViewDisciplinary = usePermission('disciplinary.view');
  const canViewGrievance = usePermission('grievance.view');
  const canViewSafeguarding = usePermission('safeguarding.view');
  const canViewPsea = usePermission('psea.view');
  const canSeeRestrictedTab = canViewDisciplinary || canViewGrievance || canViewSafeguarding || canViewPsea;
  const visibleTabs = TABS.filter((t) => t !== 'Restricted' || canSeeRestrictedTab);

  const employeeQuery = useQuery({ queryKey: ['hr-employee', id], queryFn: () => employeeApi.getById(id) });
  const completionQuery = useQuery({ queryKey: ['hr-employee-completion', id], queryFn: () => employeeApi.getProfileCompletion(id) });
  const trackingQuery = useQuery({ queryKey: ['tracking', 'employee', id], queryFn: () => trackingApi.forEntity('employee', id) });
  const documentsQuery = useQuery({ queryKey: ['staff-documents', id], queryFn: () => employeeDocumentApi.listForEmployee(id) });
  const cardQuery = useQuery({ queryKey: ['hr-id-card', id], queryFn: () => idCardApi.getForEmployee(id) });
  const historyQuery = useQuery({ queryKey: ['hr-employment-history', id], queryFn: () => employmentHistoryApi.list(id) });
  const departmentsQuery = useQuery({ queryKey: ['admin-departments'], queryFn: () => departmentsApi.list() });
  const positionsQuery = useQuery({ queryKey: ['admin-positions'], queryFn: () => positionsApi.list() });
  const timesheetsQuery = useQuery({ queryKey: ['staff-timesheets', id], queryFn: () => timesheetApi.listForEmployee(id), enabled: activeTab === 'Timesheets' });
  const movementsQuery = useQuery({ queryKey: ['staff-movements-for-employee', id], queryFn: () => staffMovementApi.getForEmployee(id), enabled: activeTab === 'Movements' });
  const separationQuery = useQuery({ queryKey: ['staff-separation-for-employee', id], queryFn: () => separationApi.getForEmployee(id), enabled: activeTab === 'Separation' });
  const restrictedCasesQuery = useQuery({ queryKey: ['restricted-cases-for-employee', id], queryFn: () => restrictedCaseApi.getForEmployee(id), enabled: activeTab === 'Restricted' && canSeeRestrictedTab });
  const deleteTimesheetMutation = useMutation({
    mutationFn: (timesheetId: number) => timesheetApi.delete(timesheetId),
    onSuccess: () => { showSuccess('Timesheet deleted.'); queryClient.invalidateQueries({ queryKey: ['staff-timesheets', id] }); },
    onError: (err) => showError(err instanceof ApiError ? err.message : 'Failed to delete timesheet.'),
  });

  /** Real gap closed (Staff Workspace audit): the admin-side add/remove-contact endpoints and API client methods already existed - the UI to actually use them from HR's side was the only missing piece, mirroring the same gap just fixed on My Profile. */
  const { register: registerHrContact, handleSubmit: handleSubmitHrContact, reset: resetHrContact } = useForm<{ contactType: string; fullName: string; relationship: string; phone: string; address: string }>({
    defaultValues: { contactType: 'emergency_contact', fullName: '', relationship: '', phone: '', address: '' },
  });
  const addHrContactMutation = useMutation({
    mutationFn: (values: { contactType: string; fullName: string; relationship: string; phone: string; address: string }) =>
      employeeApi.addContact(id, { contactType: values.contactType, fullName: values.fullName, relationship: values.relationship || undefined, phone: values.phone || undefined, address: values.address || undefined }),
    onSuccess: () => { showSuccess('Contact added.'); queryClient.invalidateQueries({ queryKey: ['hr-employee', id] }); resetHrContact(); },
    onError: (err) => showError(err instanceof ApiError ? err.message : 'Failed to add contact.'),
  });
  const removeHrContactMutation = useMutation({
    mutationFn: (contactId: number) => employeeApi.removeContact(id, contactId),
    onSuccess: () => { showSuccess('Contact removed.'); queryClient.invalidateQueries({ queryKey: ['hr-employee', id] }); },
    onError: (err) => showError(err instanceof ApiError ? err.message : 'Failed to remove contact.'),
  });

  const identificationQuery = useQuery({ queryKey: ['staff-identification', id], queryFn: () => identificationApi.get(id), enabled: activeTab === 'Personal' });
  const qualificationsQuery = useQuery({ queryKey: ['staff-qualifications', id], queryFn: () => qualificationApi.listForEmployee(id), enabled: activeTab === 'Personal' });
  const compensationHistoryQuery = useQuery({ queryKey: ['staff-compensation-history', id], queryFn: () => compensationHistoryApi.list(id), enabled: activeTab === 'Personal' && canViewCompensation });
  const projectAssignmentsQuery = useQuery({ queryKey: ['staff-project-assignments', id], queryFn: () => projectAssignmentApi.listForEmployee(id), enabled: activeTab === 'Projects' });
  const projectsQuery = useQuery({ queryKey: ['projects-list'], queryFn: () => projectsApi.list(), enabled: activeTab === 'Projects' });
  const leaveQuery = useQuery({ queryKey: ['staff-leave', id], queryFn: () => leaveApi.listForEmployee(id), enabled: activeTab === 'Leave' || activeTab === 'Overview' });
  const leaveBalancesQuery = useQuery({ queryKey: ['staff-leave-balances', id], queryFn: () => leaveApi.getBalancesForEmployee(id), enabled: activeTab === 'Leave' });
  const leaveTypesQuery = useQuery({ queryKey: ['leave-types'], queryFn: () => leaveApi.types(), enabled: activeTab === 'Leave' });
  const { register: registerBalance, handleSubmit: handleSubmitBalance, reset: resetBalance } = useForm<{ leaveTypeId: string; year: string; entitledDays: string }>({
    defaultValues: { leaveTypeId: '', year: String(new Date().getFullYear()), entitledDays: '' },
  });
  const setBalanceMutation = useMutation({
    mutationFn: (values: { leaveTypeId: string; year: string; entitledDays: string }) =>
      leaveApi.setBalance(id, { leaveTypeId: Number(values.leaveTypeId), year: Number(values.year), entitledDays: Number(values.entitledDays) }),
    onSuccess: () => { showSuccess('Leave entitlement set.'); queryClient.invalidateQueries({ queryKey: ['staff-leave-balances', id] }); resetBalance(); },
    onError: (err) => showError(err instanceof ApiError ? err.message : 'Failed to set entitlement.'),
  });
  const trainingQuery = useQuery({ queryKey: ['staff-training', id], queryFn: () => trainingApi.listForEmployee(id), enabled: activeTab === 'Training' || activeTab === 'Overview' });
  const performanceQuery = useQuery({ queryKey: ['staff-performance', id], queryFn: () => performanceApi.listForEmployee(id), enabled: activeTab === 'Performance' || activeTab === 'Overview' });

  const { register, handleSubmit } = useForm<Partial<Employee>>({ values: employeeQuery.data?.data });

  const saveEmploymentMutation = useMutation({
    mutationFn: (values: Partial<Employee>) =>
      employeeApi.updateEmployment(id, {
        grade: values.grade, employmentType: values.employment_type, employmentStatus: values.employment_status,
        departmentId: values.department_id ? Number(values.department_id) : null, positionId: values.position_id ? Number(values.position_id) : null,
        supervisorId: values.supervisor_id ? Number(values.supervisor_id) : null,
      }),
    onSuccess: () => { showSuccess('Employment details saved.'); queryClient.invalidateQueries({ queryKey: ['hr-employee', id] }); queryClient.invalidateQueries({ queryKey: ['hr-employee-completion', id] }); },
    onError: (err) => showError(err instanceof ApiError ? err.message : 'Failed to save employment details.'),
  });

  const generateStaffIdMutation = useMutation({
    mutationFn: () => employeeApi.generateStaffId(id),
    onSuccess: (result) => { showSuccess(`Staff ID generated - ${result.data.staffId}`); queryClient.invalidateQueries({ queryKey: ['hr-employee', id] }); },
    onError: (err) => showError(err instanceof ApiError ? err.message : 'Failed to save.'),
  });

  const [salaryDraft, setSalaryDraft] = useState('');
  const salaryMutation = useMutation({
    mutationFn: (amount: string) => employeeApi.updateSalary(id, { baseSalary: amount ? Number(amount) : null }),
    onSuccess: () => { showSuccess('Salary updated.'); queryClient.invalidateQueries({ queryKey: ['hr-employee', id] }); },
    onError: (err) => showError(err instanceof ApiError ? err.message : 'Failed to update salary.'),
  });

  const [issueDate, setIssueDate] = useState('');
  const [expiryDate, setExpiryDate] = useState('');
  const generateCardMutation = useMutation({
    mutationFn: () => idCardApi.generate(id, { issueDate: issueDate || undefined, expiryDate: expiryDate || undefined }),
    onSuccess: () => { showSuccess('ID card generated.'); queryClient.invalidateQueries({ queryKey: ['hr-id-card', id] }); },
    onError: (err) => showError(err instanceof ApiError ? err.message : 'Failed to generate ID card.'),
  });

  const deactivateCardMutation = useMutation({
    mutationFn: (cardId: number) => idCardApi.deactivate(id, cardId),
    onSuccess: () => { showSuccess('ID card deactivated.'); queryClient.invalidateQueries({ queryKey: ['hr-id-card', id] }); },
    onError: (err) => showError(err instanceof ApiError ? err.message : 'Failed to deactivate ID card.'),
  });

  if (!employeeQuery.data) return <div className="page"><p className="muted">Loading…</p></div>;
  const employee = employeeQuery.data.data;
  const nextOfKin = employeeQuery.data.contacts.filter((c) => c.contact_type === 'next_of_kin');
  const emergencyContacts = employeeQuery.data.contacts.filter((c) => c.contact_type !== 'next_of_kin');
  const completion = completionQuery.data?.data;

  return (
    <div className="page">
      <Link to="/hr/staff">&larr; Back to Staff Management</Link>

      <div className="staff-header">
        <div className="staff-header-main">
          {employee.profile_photo_data ? (
            <img src={employee.profile_photo_data} alt={employee.full_name} className="staff-header-photo" />
          ) : (
            <div className="staff-header-photo-fallback">
              {employee.full_name.split(' ').map((p) => p[0]).slice(0, 2).join('').toUpperCase()}
            </div>
          )}
          <div className="staff-header-identity">
            <div className="staff-header-name-row">
              <h1 className="staff-header-name">{employee.full_name}</h1>
              <StatusBadge status={employee.employment_status} />
            </div>
            <div className="staff-header-staffid">
              {employee.staff_id ?? (
                <>
                  Staff ID not yet assigned
                  <button className="link-button" onClick={() => generateStaffIdMutation.mutate()} disabled={generateStaffIdMutation.isPending}>
                    {generateStaffIdMutation.isPending ? ' Generating…' : ' Generate Staff ID'}
                  </button>
                </>
              )}
            </div>
            <div className="staff-header-position">{employee.position_name ?? 'Position not set'}</div>
            <div className="staff-header-department">{employee.department_name ?? 'Department not set'}</div>
            <div className="staff-header-meta-row">
              <span className="staff-header-meta-item">Duty Station: <strong>{employee.duty_station_name ?? '-'}</strong></span>
              <span className="staff-header-meta-item">Supervisor: <strong>{employee.supervisor_name ?? '-'}</strong></span>
              <span className="staff-header-meta-item">Employment: <strong>{employee.employment_type ?? '-'}</strong></span>
            </div>
          </div>
          <div className="staff-header-actions">
            {canManage && <button className="btn-secondary" onClick={() => setActiveTab('Employment')}>Edit</button>}
          </div>
        </div>
        {(() => {
          const entries = trackingQuery.data?.data ?? [];
          const createdEntry = entries.length > 0 ? entries[entries.length - 1] : null;
          const updatedEntry = entries.length > 0 ? entries[0] : null;
          if (!createdEntry && !updatedEntry) return null;
          return (
            <div className="record-header-footer">
              <span>{createdEntry ? <>Created by <strong>{createdEntry.actor_name ?? 'System'}</strong> · {formatDate(createdEntry.created_at)}</> : null}</span>
              <span>{updatedEntry && updatedEntry !== createdEntry ? <>Last updated by <strong>{updatedEntry.actor_name ?? 'System'}</strong> · {formatDate(updatedEntry.created_at)}</> : null}</span>
            </div>
          );
        })()}
      </div>
      {!canManage && <ReadOnlyBanner />}
      {completion && (
        <div className="completion-ring-wrap">
          <CompletionRing percentage={completion.percentage} />
          <span className="muted">Profile: {completion.filled}/{completion.total} fields</span>
        </div>
      )}

      <nav className="workspace-tabs">
        {visibleTabs.map((tab) => (
          <button key={tab} className={'workspace-tab' + (activeTab === tab ? ' workspace-tab-active' : '')} onClick={() => setActiveTab(tab)}>{tab}</button>
        ))}
      </nav>

      {activeTab === 'Overview' && (
        <>
          <section className="card">
            <h2>Emergency Contact</h2>
            {emergencyContacts.length === 0 && <p className="muted">None on file.</p>}
            <ul className="deadline-list">
              {emergencyContacts.map((c) => (
                <li key={c.id}>
                  {c.full_name} {c.relationship && `(${c.relationship})`} {c.phone && `· ${c.phone}`}
                  {' '}<IconActionButton icon={Trash2} label="Remove" danger disabled={removeHrContactMutation.isPending} onClick={() => removeHrContactMutation.mutate(c.id)} />
                </li>
              ))}
            </ul>
          </section>
          <section className="card">
            <h2>Next of Kin</h2>
            {nextOfKin.length === 0 && <p className="muted">None on file.</p>}
            <ul className="deadline-list">
              {nextOfKin.map((c) => (
                <li key={c.id}>
                  {c.full_name} {c.relationship && `(${c.relationship})`} {c.phone && `· ${c.phone}`}
                  {' '}<IconActionButton icon={Trash2} label="Remove" danger disabled={removeHrContactMutation.isPending} onClick={() => removeHrContactMutation.mutate(c.id)} />
                </li>
              ))}
            </ul>
          </section>
          <section className="card">
            <h2>Add Emergency Contact / Next of Kin</h2>
            <form className="inline-form" onSubmit={handleSubmitHrContact((values) => addHrContactMutation.mutate(values))}>
              <label>
                Type
                <select {...registerHrContact('contactType')}>
                  <option value="emergency_contact">Emergency Contact</option>
                  <option value="next_of_kin">Next of Kin</option>
                </select>
              </label>
              <label>Full Name<input {...registerHrContact('fullName', { required: true })} /></label>
              <label>Relationship<input {...registerHrContact('relationship')} /></label>
              <label>Phone<input {...registerHrContact('phone')} /></label>
              <label>Address<input {...registerHrContact('address')} /></label>
              <button type="submit" disabled={addHrContactMutation.isPending}>{addHrContactMutation.isPending ? 'Adding…' : 'Add Contact'}</button>
            </form>
          </section>
          <section className="card">
            <h2>Quick Summary</h2>
            <p className="muted">
              Leave requests: {leaveQuery.data?.data.length ?? '…'} · Training records: {trainingQuery.data?.data.length ?? '…'} · Appraisals: {performanceQuery.data?.data.length ?? '…'}
            </p>
          </section>
          <CommentsThread entityType="employee" entityId={id} section="overview" heading="Comments for Overview" />
        </>
      )}

      {activeTab === 'Employment' && (
        <>
          <form onSubmit={handleSubmit((values) => saveEmploymentMutation.mutate(values))}>
            <section className="card">
              <h2>Employment</h2>
              <div className="project-header-grid">
                <StaticField label="Current Position" value={employee.position_name ?? '-'} />
                <StaticField label="Current Department" value={employee.department_name ?? '-'} />
                <StaticField label="Duty Station" value={employee.duty_station_name ?? '-'} />
                <StaticField label="Supervisor" value={employee.supervisor_name ?? '-'} />
                <StaticField label="Start Date" value={employee.start_date ?? '-'} />
              </div>
              <label>
                Department
                <select {...register('department_id')}>
                  <option value="">- not set -</option>
                  {departmentsQuery.data?.data.map((d) => <option key={d.id} value={d.id}>{d.name}</option>)}
                </select>
              </label>
              <label>
                Position
                <select {...register('position_id')}>
                  <option value="">- not set -</option>
                  {positionsQuery.data?.data.map((p) => <option key={p.id} value={p.id}>{p.title}</option>)}
                </select>
              </label>
              <label>
                Supervisor
                <EmployeePicker {...register('supervisor_id')} excludeId={employee.id} />
              </label>
              <label>Grade<input {...register('grade')} /></label>
              <label>
                Employment Type
                <select {...register('employment_type')}>
                  <option value="">- not set -</option>
                  <option value="full_time">Full Time</option><option value="part_time">Part Time</option>
                  <option value="consultant">Consultant</option><option value="intern">Intern</option><option value="volunteer">Volunteer</option>
                </select>
              </label>
              <label>
                Employment Status
                <select {...register('employment_status')}>
                  <option value="active">Active</option><option value="on_leave">On Leave</option>
                  <option value="probation">Probation</option><option value="suspended">Suspended</option><option value="separated">Separated</option>
                </select>
              </label>
              <div className="form-actions"><button type="submit" className="btn-primary" disabled={saveEmploymentMutation.isPending}>{saveEmploymentMutation.isPending ? 'Saving…' : 'Save'}</button></div>
            </section>
          </form>

          <section className="card">
            <h2><IdCardIcon size={16} className="inline-icon" /> ID Card</h2>
            {cardQuery.data?.data ? (
              <>
                <p>Status: <strong>{cardQuery.data.data.status.replace('_', ' ')}</strong> · Issued: {cardQuery.data.data.issue_date ?? '-'} · Expires: {cardQuery.data.data.expiry_date ?? 'N/A'}</p>
                {cardQuery.data.data.status === 'active' && (
                  <button className="button-danger" onClick={() => deactivateCardMutation.mutate(cardQuery.data!.data!.id)} disabled={deactivateCardMutation.isPending}>Deactivate Card</button>
                )}
              </>
            ) : (
              <p className="muted">No ID card issued yet.</p>
            )}
            <div className="inline-form">
              <label>Issue Date<input type="date" value={issueDate} onChange={(e) => setIssueDate(e.target.value)} /></label>
              <label>Expiry Date<input type="date" value={expiryDate} onChange={(e) => setExpiryDate(e.target.value)} /></label>
              <button onClick={() => generateCardMutation.mutate()} disabled={!employee.staff_id || generateCardMutation.isPending}>
                {generateCardMutation.isPending ? 'Generating…' : 'Generate New Card'}
              </button>
            </div>
            {!employee.staff_id && <p className="muted">This employee has no Staff ID yet - a card cannot be generated.</p>}
          </section>

          <section className="card">
            <h2>Employment History</h2>
            {historyQuery.data && (
              <DataTable
                rows={historyQuery.data.data}
                rowKey={(h) => h.id}
                emptyMessage="No transfers, promotions, or amendments recorded yet."
                columns={[
                  { header: 'Date', render: (h) => h.effective_date },
                  { header: 'Type', render: (h) => h.change_type.replace('_', ' ') },
                  { header: 'Approved By', render: (h) => h.approved_by_name ?? '-' },
                  { header: 'Notes', render: (h) => h.notes ?? '-' },
                ]}
              />
            )}
          </section>

          <section className="card">
            <h2>Compensation (Payroll Reference)</h2>
            {!canViewCompensation ? (
              <p className="muted">Restricted - requires compensation access.</p>
            ) : (
              <>
                <p className="muted">Bank: {employee.bank_name ?? '-'} · Account: {employee.bank_account_number ?? '-'}</p>
                <div className="inline-form">
                  <label>
                    Salary Override
                    <input type="number" placeholder={employee.base_salary ? undefined : 'Using grade default'} defaultValue={employee.base_salary ?? ''} onChange={(e) => setSalaryDraft(e.target.value)} />
                  </label>
                  <button onClick={() => salaryMutation.mutate(salaryDraft)} disabled={salaryMutation.isPending}>Save Salary</button>
                </div>
              </>
            )}
          </section>
          <CommentsThread entityType="employee" entityId={id} section="employment" heading="Comments for Employment" />
        </>
      )}

      {activeTab === 'Contracts' && (
        <>
          <ContractsSection employeeId={id} />
          <CommentsThread entityType="employee" entityId={id} section="contracts" heading="Comments for Contracts" />
        </>
      )}

      {activeTab === 'Timesheets' && (
        <>
          <section className="card">
            <h2>Timesheets</h2>
            {timesheetsQuery.data && (
              <DataTable
                rows={timesheetsQuery.data.data}
                rowKey={(t) => t.id}
                emptyMessage="No timesheets recorded yet."
                columns={[
                  { header: 'Month', render: (t) => t.period_month },
                  { header: 'Days Worked', render: (t) => t.days_worked ?? '-' },
                  { header: 'Status', render: (t) => <StatusBadge status={t.status} /> },
                  { header: '', render: (t) => canDeleteTimesheets && t.status === 'draft' && <IconActionButton icon={Trash2} label="Delete" danger onClick={() => { if (window.confirm('Delete this timesheet?')) deleteTimesheetMutation.mutate(t.id); }} /> },
                ]}
              />
            )}
          </section>
          <CommentsThread entityType="employee" entityId={id} section="timesheets" heading="Comments for Timesheets" />
        </>
      )}

      {activeTab === 'Movements' && (
        <section className="card">
          <h2>Staff Movements</h2>
          <p className="muted">Formal employment movements - promotions, transfers, secondments, and changes of department, position, duty station, or supervisor.</p>
          <DataTable
            rows={movementsQuery.data?.data ?? []}
            rowKey={(m) => m.id}
            emptyMessage="No staff movements recorded yet."
            columns={[
              { header: 'Effective Date', render: (m) => formatDate(m.effective_date) },
              { header: 'Movement', render: (m) => <Link to={`/hr/staff-movement/${m.id}`}>{m.movement_type.replace('_', ' ')}</Link> },
              { header: 'Previous', render: (m) => m.current_position_name ?? '-' },
              { header: 'New', render: (m) => m.proposed_position_name ?? '-' },
              { header: 'Status', render: (m) => <StatusBadge status={m.status} /> },
            ]}
          />
        </section>
      )}

      {activeTab === 'Separation' && (
        <section className="card">
          <h2>Separation</h2>
          <p className="muted">A restricted, sensitive record. Full separation administration (final settlement, clearances, exit interview) happens in <Link to="/hr/separation">Separation Management</Link>.</p>
          <DataTable
            rows={separationQuery.data?.data ?? []}
            rowKey={(s) => s.id}
            emptyMessage="No separation case on file."
            columns={[
              { header: 'Reference', render: (s) => s.separation_reference },
              { header: 'Type', render: (s) => s.separation_type.replace('_', ' ') },
              { header: 'Last Working Day', render: (s) => formatDate(s.last_working_day) },
              { header: 'Status', render: (s) => <StatusBadge status={s.status} /> },
            ]}
          />
        </section>
      )}

      {activeTab === 'Restricted' && (
        <section className="card">
          <h2>Restricted Cases</h2>
          <p className="muted">
            Disciplinary, grievance, safeguarding, and PSEA cases are separately, individually permissioned - you only ever see
            case types you are specifically authorized for. Full case details, investigation notes, and actions are handled in{' '}
            <Link to="/hr/restricted-cases">Restricted Case Management</Link>, never here.
          </p>
          <DataTable
            rows={restrictedCasesQuery.data?.data ?? []}
            rowKey={(c) => c.id}
            emptyMessage="No restricted cases visible to you for this employee."
            columns={[
              { header: 'Reference', render: (c) => c.case_reference },
              { header: 'Type', render: (c) => c.case_type },
              { header: 'Status', render: (c) => <StatusBadge status={c.status} /> },
              { header: 'Reported', render: (c) => formatDate(c.reported_at) },
            ]}
          />
        </section>
      )}

      {activeTab === 'Leave' && (
        <>
        <section className="card">
          <h2>Leave Balances ({new Date().getFullYear()})</h2>
          {leaveBalancesQuery.data && (
            <DataTable
              rows={leaveBalancesQuery.data.data}
              rowKey={(b) => b.id}
              emptyMessage="No leave entitlement set for this year yet - use the form below to set one."
              columns={[
                { header: 'Type', render: (b) => b.leave_type_name },
                { header: 'Entitled', render: (b) => b.entitled_days },
                { header: 'Used', render: (b) => b.used_days },
                { header: 'Remaining', render: (b) => (Number(b.entitled_days) - Number(b.used_days)).toFixed(1) },
              ]}
            />
          )}
          <form className="inline-form" onSubmit={handleSubmitBalance((values) => setBalanceMutation.mutate(values))}>
            <label>
              Leave Type
              <select {...registerBalance('leaveTypeId', { required: true })}>
                <option value="">- choose -</option>
                {leaveTypesQuery.data?.data.map((t) => <option key={t.id} value={t.id}>{t.name}</option>)}
              </select>
            </label>
            <label>Year<input type="number" {...registerBalance('year', { required: true })} /></label>
            <label>Entitled Days<input type="number" step="0.5" {...registerBalance('entitledDays', { required: true })} /></label>
            <button type="submit" disabled={setBalanceMutation.isPending}>{setBalanceMutation.isPending ? 'Saving…' : 'Set Entitlement'}</button>
          </form>
        </section>
        <section className="card">
          <h2>Leave History</h2>
          {leaveQuery.data && (
            <DataTable
              rows={leaveQuery.data.data}
              rowKey={(l) => l.id}
              emptyMessage="No leave requests yet."
              columns={[
                { header: 'Type', render: (l) => l.leave_type_name ?? '-' },
                { header: 'Dates', render: (l) => `${formatDate(l.start_date)} - ${formatDate(l.end_date)}` },
                { header: 'Status', render: (l) => <StatusBadge status={l.status} /> },
              ]}
            />
          )}
        </section>
        <CommentsThread entityType="employee" entityId={id} section="leave" heading="Comments for Leave" />
        </>
      )}

      {activeTab === 'Training' && (
        <>
        <section className="card">
          <h2>Training History</h2>
          {trainingQuery.data && (
            <DataTable
              rows={trainingQuery.data.data}
              rowKey={(t) => t.id}
              emptyMessage="No training records yet."
              columns={[
                { header: 'Title', render: (t) => t.catalogue_title ?? t.training_title ?? '-' },
                { header: 'Status', render: (t) => <StatusBadge status={t.status} /> },
                { header: 'Completed', render: (t) => t.completed_at ?? '-' },
              ]}
            />
          )}
        </section>
        <CommentsThread entityType="employee" entityId={id} section="training" heading="Comments for Training" />
        </>
      )}

      {activeTab === 'Performance' && (
        <>
        <section className="card">
          <h2>Performance History</h2>
          {performanceQuery.data && (
            <DataTable
              rows={performanceQuery.data.data}
              rowKey={(p) => p.id}
              emptyMessage="No appraisals yet."
              columns={[
                { header: 'Cycle', render: (p) => p.cycle_name },
                { header: 'Status', render: (p) => <StatusBadge status={p.status} /> },
                { header: 'Final Rating', render: (p) => p.final_rating ?? '-' },
              ]}
            />
          )}
        </section>
        <CommentsThread entityType="employee" entityId={id} section="performance" heading="Comments for Performance" />
        </>
      )}

      {activeTab === 'Personal' && (
        <>
        <MasterRecordTab
          employeeId={id}
          identification={identificationQuery.data?.data ?? null}
          qualifications={qualificationsQuery.data?.data ?? []}
          compensationHistory={compensationHistoryQuery.data?.data ?? []}
          canViewCompensation={canViewCompensation}
        />
        <CommentsThread entityType="employee" entityId={id} section="master_record" heading="Comments for Personal" />
        </>
      )}

      {activeTab === 'Projects' && (
        <>
        <ProjectAssignmentsTab
          employeeId={id}
          projectAssignments={projectAssignmentsQuery.data?.data ?? []}
          projects={projectsQuery.data?.data ?? []}
        />
        <CommentsThread entityType="employee" entityId={id} section="project_assignments" heading="Comments for Projects" />
        </>
      )}

      {activeTab === 'Onboarding' && (
        <>
          <OnboardingTab employeeId={id} />
          <CommentsThread entityType="employee" entityId={id} section="onboarding" heading="Comments for Onboarding" />
        </>
      )}

      {activeTab === 'Probation' && (
        <>
          <ProbationTab employeeId={id} />
          <CommentsThread entityType="employee" entityId={id} section="probation" heading="Comments for Probation" />
        </>
      )}

      {activeTab === 'Tracking' && <WorkflowTrackingTab entityType="employee" entityId={id} actionStageMap={EMPLOYEE_STAGES} />}

      {activeTab === 'Documents' && documentsQuery.data && (
        <DocumentsSection
          documents={documentsQuery.data.data}
          downloadUrl={(documentId) => employeeDocumentApi.downloadForEmployeeUrl(id, documentId)}
          upload={(input) => employeeDocumentApi.uploadForEmployee(id, input)}
          onDelete={(documentId) => employeeDocumentApi.deleteForEmployee(id, documentId)}
          queryKeyToInvalidate={['staff-documents', id]}
        />
      )}

    </div>
  );
}

function CompletionRing({ percentage }: { percentage: number }) {
  const radius = 28;
  const circumference = 2 * Math.PI * radius;
  const offset = circumference - (percentage / 100) * circumference;
  return (
    <svg width="70" height="70" viewBox="0 0 70 70">
      <circle cx="35" cy="35" r={radius} fill="none" stroke="#e5e7eb" strokeWidth="6" />
      <circle cx="35" cy="35" r={radius} fill="none" stroke="#15803d" strokeWidth="6" strokeDasharray={circumference} strokeDashoffset={offset} strokeLinecap="round" transform="rotate(-90 35 35)" />
      <text x="35" y="40" textAnchor="middle" fontSize="16" fontWeight="700" fill="#15803d">{percentage}%</text>
    </svg>
  );
}

function StaticField({ label, value }: { label: string; value: string }) {
  return (
    <div className="project-header-field">
      <div className="project-header-field-label">{label}</div>
      <div className="project-header-field-value">{value}</div>
    </div>
  );
}

const CONTRACT_STATUS_LABELS: Record<string, string> = {
  draft: 'Draft', hr_review: 'HR Review / Pending Approval', returned: 'Returned for Redraft', approved: 'Approved',
  signed: 'Signed', finalised: 'Finalised', expired: 'Expired', terminated: 'Terminated', rejected: 'Rejected',
};

/** Prefilled data review (Contract Management) - grouped for readability, matching the approved mockup structure. Signature fields deliberately excluded - those aren't filled until sign()/finalise() are actually called. */
const CONTRACT_PREVIEW_GROUPS: { title: string; fields: string[] }[] = [
  { title: 'Personal', fields: ['staff_name', 'staff_id', 'phone', 'email', 'residential_address', 'national_id'] },
  { title: 'Position and employment', fields: ['position', 'department', 'duty_station', 'state', 'supervisor_name', 'probation_period'] },
  { title: 'Compensation and banking', fields: ['monthly_gross_salary', 'bank_name', 'bank_account_number', 'bank_account_name'] },
  { title: 'Emergency contact', fields: ['emergency_contact'] },
];
const CONTRACT_PREVIEW_LABELS: Record<string, string> = {
  staff_name: 'Name', staff_id: 'Staff ID', phone: 'Phone', email: 'Email', residential_address: 'Address', national_id: 'National ID',
  position: 'Position', department: 'Department', duty_station: 'Duty station', state: 'State', supervisor_name: 'Supervisor', probation_period: 'Probation period',
  monthly_gross_salary: 'Monthly salary', bank_name: 'Bank name', bank_account_number: 'Account number', bank_account_name: 'Account name',
  emergency_contact: 'Emergency contact',
};

function ContractPreviewFields({ fields }: { fields: Record<string, string> }) {
  return (
    <>
      {CONTRACT_PREVIEW_GROUPS.map((group) => (
        <div key={group.title} style={{ marginBottom: '0.75rem' }}>
          <p style={{ fontWeight: 500, fontSize: '0.85em', margin: '0 0 4px' }}>{group.title}</p>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '4px 12px', fontSize: '0.9em' }}>
            {group.fields.map((key) => (
              <Fragment key={key}>
                <span className="muted">{CONTRACT_PREVIEW_LABELS[key]}</span>
                <span className={!fields[key] ? 'error-text' : undefined}>{fields[key] || 'Missing'}</span>
              </Fragment>
            ))}
          </div>
        </div>
      ))}
    </>
  );
}

function ContractsSection({ employeeId }: { employeeId: number }) {
  const navigate = useNavigate();
  const { showSuccess, showError } = useToast();
  const queryClient = useQueryClient();
  const [showForm, setShowForm] = useState(false);

  const contractsQuery = useQuery({ queryKey: ['contracts', employeeId], queryFn: () => contractApi.listForEmployee(employeeId) });
  const templatesQuery = useQuery({ queryKey: ['contract-templates'], queryFn: () => contractTemplateApi.list(), enabled: showForm });
  const previewQuery = useQuery({ queryKey: ['contract-preview-fields', employeeId], queryFn: () => contractApi.previewFields(employeeId), enabled: showForm });
  const { register, handleSubmit, reset } = useForm<Record<string, string>>();

  const createMutation = useMutation({
    mutationFn: (values: Record<string, string>) => contractApi.create(employeeId, { templateId: values.templateId ? Number(values.templateId) : null, startDate: values.startDate, endDate: values.endDate || null }),
    onSuccess: (result) => {
      showSuccess(`Contract created - ${result.data.contractReference}`);
      queryClient.invalidateQueries({ queryKey: ['contracts', employeeId] });
      setShowForm(false);
      reset();
    },
    onError: (err) => showError(err instanceof ApiError ? err.message : 'Failed to create contract.'),
  });

  /**
   * Contract Workspace architecture - this section is now just a
   * summary and the creation entry point. Every workflow action
   * (edit, submit, decide, sign, finalise, download, comments,
   * documents, tracking) lives in the real workspace at
   * /hr/contracts/:id, not duplicated here.
   *
   * Prefilled data review (real gap closed): the 17 employee-sourced
   * fields that get auto-filled into the generated document are now
   * shown here BEFORE creating, not just discoverable afterward by
   * reading the document - including a genuine warning when data is
   * actually missing from the employee's record, with a direct link
   * to fix it instead of generating a contract full of blanks.
   */
  return (
    <section className="card">
      <div className="page-header"><h2>Contracts</h2><button onClick={() => setShowForm((v) => !v)}>{showForm ? 'Cancel' : 'New Contract'}</button></div>

      {showForm && (
        <>
          <form className="inline-form" onSubmit={handleSubmit((values) => createMutation.mutate(values))}>
            <label>
              Template
              <select {...register('templateId')}>
                <option value="">- no template -</option>
                {templatesQuery.data?.data.map((t) => <option key={t.id} value={t.id}>{t.name}</option>)}
              </select>
            </label>
            <label>Start Date<input type="date" {...register('startDate', { required: true })} /></label>
            <label>End Date<input type="date" {...register('endDate')} /></label>

            {previewQuery.data && (
              <div className="panel" style={{ marginTop: '1rem' }}>
                <div className="panel-body">
                  <p className="muted">This will be pulled from the employee's record when the contract is created:</p>
                  <ContractPreviewFields fields={previewQuery.data.data.fields} />

                  {previewQuery.data.data.missingFields.length > 0 && (
                    <div className="error-text" style={{ marginTop: '0.75rem' }}>
                      {previewQuery.data.data.missingFields.length} field(s) missing from this employee's record - the generated document will show blanks for these unless added first: {previewQuery.data.data.missingFields.map((f) => CONTRACT_PREVIEW_LABELS[f] ?? f).join(', ')}.
                      {' '}<Link to={`/hr/staff/${employeeId}`}>Edit employee record</Link>
                    </div>
                  )}
                </div>
              </div>
            )}

            <button type="submit" disabled={createMutation.isPending}>{createMutation.isPending ? 'Creating…' : 'Confirm and Create'}</button>
          </form>
        </>
      )}


      {contractsQuery.data && (
        <DataTable
          rows={contractsQuery.data.data}
          rowKey={(c) => c.id}
          emptyMessage="No contracts yet."
          columns={[
            { header: 'Reference', render: (c) => <Link to={`/hr/contracts/${c.id}`}>{c.contract_reference}</Link> },
            { header: 'Start', render: (c) => c.start_date },
            { header: 'End', render: (c) => c.end_date ?? 'Open-ended' },
            { header: 'Status', render: (c) => <StatusBadge status={c.status} label={CONTRACT_STATUS_LABELS[c.status]} /> },
            { header: '', render: (c) => <IconActionButton icon={Eye} label="Open Workspace" onClick={() => navigate(`/hr/contracts/${c.id}`)} /> },
          ]}
        />
      )}
    </section>
  );
}


const QUALIFICATION_TYPE_LABELS: Record<string, string> = { degree: 'Degree', certification: 'Certification', membership: 'Professional Membership', language: 'Language' };

function MasterRecordTab({
  employeeId, identification, qualifications, compensationHistory, canViewCompensation,
}: {
  employeeId: number;
  identification: import('../../api/staff-master-record').EmployeeIdentification | null;
  qualifications: import('../../api/staff-master-record').EmployeeQualification[];
  compensationHistory: import('../../api/staff-master-record').CompensationHistoryEntry[];
  canViewCompensation: boolean;
}) {
  const { showSuccess, showError } = useToast();
  const queryClient = useQueryClient();

  // ---- Identification ----
  const idForm = useForm<Record<string, string>>({ values: identification ? Object.fromEntries(Object.entries(identification).map(([k, v]) => [k, v ?? ''])) : undefined });
  const saveIdMutation = useMutation({
    mutationFn: (values: Record<string, string>) => identificationApi.upsert(employeeId, values),
    onSuccess: () => { showSuccess('Identification details saved.'); queryClient.invalidateQueries({ queryKey: ['staff-identification', employeeId] }); },
    onError: (err) => showError(err instanceof ApiError ? err.message : 'Failed to save.'),
  });

  // ---- Qualifications ----
  const qualForm = useForm<{ qualificationType: string; title: string; institution?: string; fieldOfStudy?: string; yearObtained?: string }>();
  const addQualMutation = useMutation({
    mutationFn: (v: { qualificationType: string; title: string; institution?: string; fieldOfStudy?: string; yearObtained?: string }) =>
      qualificationApi.createForEmployee(employeeId, { ...v, yearObtained: v.yearObtained ? Number(v.yearObtained) : undefined }),
    onSuccess: () => { showSuccess('Qualification added.'); queryClient.invalidateQueries({ queryKey: ['staff-qualifications', employeeId] }); qualForm.reset(); },
    onError: (err) => showError(err instanceof ApiError ? err.message : 'Failed to add.'),
  });
  const deleteQualMutation = useMutation({
    mutationFn: (qualId: number) => qualificationApi.deleteForEmployee(employeeId, qualId),
    onSuccess: () => { showSuccess('Qualification removed.'); queryClient.invalidateQueries({ queryKey: ['staff-qualifications', employeeId] }); },
    onError: (err) => showError(err instanceof ApiError ? err.message : 'Failed to remove.'),
  });

  // ---- Project Assignments (extracted to ProjectAssignmentsTab) ----

  return (
    <>
      <section className="card">
        <h2>Identification</h2>
        <p className="muted">Restricted access - visible only to HR Manager and Admin.</p>
        <form onSubmit={idForm.handleSubmit((v) => saveIdMutation.mutate(v))}>
          <label>National ID Type<input {...idForm.register('national_id_type')} /></label>
          <label>National ID Number<input {...idForm.register('national_id_number')} /></label>
          <label>Passport Number<input {...idForm.register('passport_number')} /></label>
          <label>Passport Expiry<input type="date" {...idForm.register('passport_expiry_date')} /></label>
          <label>Driver's Licence Number<input {...idForm.register('drivers_licence_number')} /></label>
          <label>Driver's Licence Expiry<input type="date" {...idForm.register('drivers_licence_expiry_date')} /></label>
          <label>Other Identification<textarea rows={2} {...idForm.register('other_identification')} /></label>
          <div className="form-actions"><button type="submit" className="btn-primary" disabled={saveIdMutation.isPending}>{saveIdMutation.isPending ? 'Saving…' : 'Save'}</button></div>
        </form>
      </section>

      <section className="card">
        <h2>Qualifications</h2>
        <form className="inline-form" onSubmit={qualForm.handleSubmit((v) => addQualMutation.mutate(v))}>
          <label>
            Type
            <select {...qualForm.register('qualificationType', { required: true })}>
              <option value="degree">Degree</option><option value="certification">Certification</option>
              <option value="membership">Professional Membership</option><option value="language">Language</option>
            </select>
          </label>
          <label>Title<input {...qualForm.register('title', { required: true })} /></label>
          <label>Institution<input {...qualForm.register('institution')} /></label>
          <label>Field of Study<input {...qualForm.register('fieldOfStudy')} /></label>
          <label>Year<input type="number" {...qualForm.register('yearObtained')} /></label>
          <button type="submit" disabled={addQualMutation.isPending}>Add</button>
        </form>
        <DataTable
          rows={qualifications}
          rowKey={(q) => q.id}
          emptyMessage="No qualifications recorded yet."
          columns={[
            { header: 'Type', render: (q) => QUALIFICATION_TYPE_LABELS[q.qualification_type] },
            { header: 'Title', render: (q) => q.title },
            { header: 'Institution', render: (q) => q.institution ?? '-' },
            { header: 'Year', render: (q) => q.year_obtained ?? '-' },
            { header: '', render: (q) => <IconActionButton icon={Trash2} label="Remove" danger onClick={() => deleteQualMutation.mutate(q.id)} /> },
          ]}
        />
      </section>

      <section className="card">
        <h2>Compensation History</h2>
        {!canViewCompensation ? (
          <p className="muted">Restricted - requires compensation access.</p>
        ) : (
          <>
            <p className="muted">A permanent record - never edited, only appended to whenever salary changes.</p>
            <DataTable
              rows={compensationHistory}
              rowKey={(c) => c.id}
              emptyMessage="No compensation changes recorded yet."
              columns={[
                { header: 'Effective Date', render: (c) => c.effective_date },
                { header: 'Previous', render: (c) => c.previous_base_salary ? `${c.currency} ${Number(c.previous_base_salary).toLocaleString()}` : '-' },
                { header: 'New', render: (c) => `${c.currency} ${Number(c.new_base_salary).toLocaleString()}` },
                { header: 'Reason', render: (c) => c.reason ?? '-' },
                { header: 'Changed By', render: (c) => c.changed_by_name },
              ]}
            />
          </>
        )}
      </section>

    </>
  );
}

function ProjectAssignmentsTab({
  employeeId, projectAssignments, projects,
}: {
  employeeId: number;
  projectAssignments: import('../../api/staff-master-record').ProjectAssignment[];
  projects: import('../../types/projects').Project[];
}) {
  const { showSuccess, showError } = useToast();
  const queryClient = useQueryClient();

  const assignForm = useForm<{ projectId: string; roleOnProject?: string; allocationPercentage?: string; startDate: string }>();
  const assignMutation = useMutation({
    mutationFn: (v: { projectId: string; roleOnProject?: string; allocationPercentage?: string; startDate: string }) =>
      projectAssignmentApi.create(employeeId, { projectId: Number(v.projectId), roleOnProject: v.roleOnProject, allocationPercentage: v.allocationPercentage ? Number(v.allocationPercentage) : undefined, startDate: v.startDate }),
    onSuccess: () => { showSuccess('Project assignment added.'); queryClient.invalidateQueries({ queryKey: ['staff-project-assignments', employeeId] }); assignForm.reset(); },
    onError: (err) => showError(err instanceof ApiError ? err.message : 'Failed to add.'),
  });
  const endAssignMutation = useMutation({
    mutationFn: (assignId: number) => projectAssignmentApi.end(assignId, new Date().toISOString().slice(0, 10)),
    onSuccess: () => { showSuccess('Assignment ended.'); queryClient.invalidateQueries({ queryKey: ['staff-project-assignments', employeeId] }); },
    onError: (err) => showError(err instanceof ApiError ? err.message : 'Failed to end.'),
  });

  return (
    <>
      <section className="card">
        <h2>Project / Programme Assignments</h2>
        <form className="inline-form" onSubmit={assignForm.handleSubmit((v) => assignMutation.mutate(v))}>
          <label>
            Project
            <select {...assignForm.register('projectId', { required: true })}>
              <option value="">- select -</option>
              {projects.map((p) => <option key={p.id} value={p.id}>{p.project_name}</option>)}
            </select>
          </label>
          <label>Role<input {...assignForm.register('roleOnProject')} /></label>
          <label>Allocation %<input type="number" {...assignForm.register('allocationPercentage')} /></label>
          <label>Start Date<input type="date" {...assignForm.register('startDate', { required: true })} /></label>
          <button type="submit" disabled={assignMutation.isPending}>Assign</button>
        </form>
        <DataTable
          rows={projectAssignments}
          rowKey={(a) => a.id}
          emptyMessage="Not assigned to any project yet."
          columns={[
            { header: 'Project', render: (a) => a.project_name },
            { header: 'Role', render: (a) => a.role_on_project ?? '-' },
            { header: 'Allocation', render: (a) => a.allocation_percentage ? `${a.allocation_percentage}%` : '-' },
            { header: 'Status', render: (a) => a.is_active ? 'Active' : `Ended ${formatDate(a.end_date)}` },
            { header: '', render: (a) => a.is_active ? <IconActionButton icon={LogOut} label="End Assignment" onClick={() => endAssignMutation.mutate(a.id)} /> : null },
          ]}
        />
      </section>
    </>
  );
}

const CATEGORY_LABELS: Record<string, string> = {
  hr_documentation: 'HR Documentation', contract: 'Contract', identification_verification: 'Identification Verification',
  bank_payroll: 'Bank/Payroll', it_account_setup: 'IT/Account Setup', equipment_assets: 'Equipment/Assets',
  orientation: 'Orientation', policies_conduct: 'Policies/Code of Conduct', safeguarding_psea: 'Safeguarding/PSEA',
  health_safety: 'Health and Safety', department_induction: 'Department Induction', supervisor_responsibilities: 'Supervisor Responsibilities',
};

function OnboardingTab({ employeeId }: { employeeId: number }) {
  const { showSuccess, showError } = useToast();
  const queryClient = useQueryClient();

  const onboardingQuery = useQuery({ queryKey: ['staff-onboarding', employeeId], queryFn: () => onboardingApi.getForEmployee(employeeId) });

  const startMutation = useMutation({
    mutationFn: () => onboardingApi.createForEmployee(employeeId),
    onSuccess: () => { showSuccess('Onboarding checklist started with the default task set.'); queryClient.invalidateQueries({ queryKey: ['staff-onboarding', employeeId] }); },
    onError: (err) => showError(err instanceof ApiError ? err.message : 'Failed to start onboarding.'),
  });

  const completeTaskMutation = useMutation({
    mutationFn: ({ taskId, status }: { taskId: number; status: 'completed' | 'not_applicable' }) => onboardingApi.completeTask(taskId, { status }),
    onSuccess: () => { showSuccess('Task updated.'); queryClient.invalidateQueries({ queryKey: ['staff-onboarding', employeeId] }); },
    onError: (err) => showError(err instanceof ApiError ? err.message : 'Failed.'),
  });

  const reopenMutation = useMutation({
    mutationFn: (taskId: number) => onboardingApi.reopenTask(taskId),
    onSuccess: () => { showSuccess('Task reopened.'); queryClient.invalidateQueries({ queryKey: ['staff-onboarding', employeeId] }); },
    onError: (err) => showError(err instanceof ApiError ? err.message : 'Failed.'),
  });

  const completeChecklistMutation = useMutation({
    mutationFn: () => onboardingApi.completeChecklist(onboardingQuery.data!.data!.id),
    onSuccess: () => { showSuccess('Onboarding marked complete.'); queryClient.invalidateQueries({ queryKey: ['staff-onboarding', employeeId] }); },
    onError: (err) => showError(err instanceof ApiError ? err.message : 'Failed to complete onboarding - check every task is resolved.'),
  });

  if (!onboardingQuery.data) return <div className="card"><p className="muted">Loading…</p></div>;

  if (!onboardingQuery.data.data) {
    return (
      <section className="card">
        <h2>Onboarding</h2>
        <p className="muted">No onboarding checklist started yet.</p>
        <button onClick={() => startMutation.mutate()} disabled={startMutation.isPending}>{startMutation.isPending ? 'Starting…' : 'Start Onboarding'}</button>
      </section>
    );
  }

  const checklist = onboardingQuery.data.data;
  const tasks = onboardingQuery.data.tasks;
  const allResolved = tasks.every((t) => t.status !== 'pending');

  return (
    <section className="card">
      <div className="page-header">
        <h2>Onboarding - {checklist.status === 'completed' ? 'Completed' : 'In Progress'}</h2>
        {checklist.status === 'in_progress' && (
          <button onClick={() => completeChecklistMutation.mutate()} disabled={!allResolved || completeChecklistMutation.isPending}>
            {completeChecklistMutation.isPending ? 'Completing…' : 'Mark Onboarding Complete'}
          </button>
        )}
      </div>
      <DataTable
        rows={tasks}
        rowKey={(t) => t.id}
        emptyMessage="No tasks."
        columns={[
          { header: 'Category', render: (t) => CATEGORY_LABELS[t.category] ?? t.category },
          { header: 'Task', render: (t) => t.task_name },
          { header: 'Responsible', render: (t) => t.responsible_name ?? '-' },
          { header: 'Status', render: (t) => <StatusBadge status={t.status} /> },
          {
            header: '',
            render: (t) => (
              <span className="row-actions">
                {t.status === 'pending' && (
                  <>
                    <IconActionButton icon={Check} label="Complete" onClick={() => completeTaskMutation.mutate({ taskId: t.id, status: 'completed' })} />
                    <IconActionButton icon={MinusCircle} label="Not Applicable" onClick={() => completeTaskMutation.mutate({ taskId: t.id, status: 'not_applicable' })} />
                  </>
                )}
                {t.status !== 'pending' && <IconActionButton icon={RotateCcw} label="Reopen" onClick={() => reopenMutation.mutate(t.id)} />}
              </span>
            ),
          },
        ]}
      />
    </section>
  );
}

const PROBATION_STATUS_LABELS: Record<string, string> = {
  in_progress: 'In Progress', under_review: 'Awaiting HR Decision', confirmed: 'Confirmed', extended: 'Extended', non_confirmed: 'Not Confirmed',
};

function ProbationTab({ employeeId }: { employeeId: number }) {
  const { showSuccess, showError } = useToast();
  const queryClient = useQueryClient();

  const probationQuery = useQuery({ queryKey: ['staff-probation', employeeId], queryFn: () => probationApi.getForEmployee(employeeId) });
  const current = probationQuery.data?.data[0];
  const detailQuery = useQuery({ queryKey: ['probation-detail', current?.id], queryFn: () => probationApi.getById(current!.id), enabled: !!current });

  const decisionForm = useForm<{ decision: string; comments: string; extensionNewEndDate?: string }>();
  const decisionMutation = useMutation({
    mutationFn: (v: { decision: string; comments: string; extensionNewEndDate?: string }) =>
      probationApi.recordHrDecision(current!.id, { decision: v.decision as 'confirmed' | 'extended' | 'non_confirmed', comments: v.comments, extensionNewEndDate: v.extensionNewEndDate }),
    onSuccess: () => { showSuccess('Decision recorded.'); queryClient.invalidateQueries({ queryKey: ['staff-probation', employeeId] }); queryClient.invalidateQueries({ queryKey: ['probation-detail', current?.id] }); queryClient.invalidateQueries({ queryKey: ['hr-employee', employeeId] }); },
    onError: (err) => showError(err instanceof ApiError ? err.message : 'Failed to record decision - the supervisor may not have submitted a recommendation yet.'),
  });

  if (!probationQuery.data) return <div className="card"><p className="muted">Loading…</p></div>;
  if (!current) return <section className="card"><h2>Probation</h2><p className="muted">No probation record for this employee. Start one from the Probation &amp; Confirmation page.</p></section>;

  return (
    <section className="card">
      <h2>Probation - {PROBATION_STATUS_LABELS[current.status] ?? current.status}</h2>
      <div className="project-header-grid">
        <StaticField label="Start Date" value={formatDate(current.start_date)} />
        <StaticField label="Expected End Date" value={formatDate(current.expected_end_date)} />
        <StaticField label="Supervisor" value={current.supervisor_name ?? '-'} />
      </div>

      {detailQuery.data && (
        <>
          <h3>Objectives</h3>
          <ul className="deadline-list">{detailQuery.data.objectives.map((o) => <li key={o.id}>{o.objective}</li>)}</ul>

          <h3>Reviews</h3>
          <ul className="deadline-list">
            {detailQuery.data.reviews.map((r) => <li key={r.id}>{formatDate(r.review_date)} - {r.assessment} ({r.submitted_by_name})</li>)}
          </ul>
        </>
      )}

      {current.supervisor_recommendation && (
        <p className="muted">Supervisor recommendation: <strong>{current.supervisor_recommendation.replace('_', ' ')}</strong> - {current.supervisor_recommendation_comments}</p>
      )}

      {current.status === 'under_review' && (
        <form onSubmit={decisionForm.handleSubmit((v) => decisionMutation.mutate(v))}>
          <h3>HR Decision</h3>
          <label>
            Decision
            <select {...decisionForm.register('decision', { required: true })}>
              <option value="">- select -</option>
              <option value="confirmed">Confirm</option>
              <option value="extended">Extend</option>
              <option value="non_confirmed">Do Not Confirm</option>
            </select>
          </label>
          <label>Comments<textarea rows={2} {...decisionForm.register('comments', { required: true })} /></label>
          <label>New End Date (if extending)<input type="date" {...decisionForm.register('extensionNewEndDate')} /></label>
          <div className="form-actions"><button type="submit" className="btn-primary" disabled={decisionMutation.isPending}>Record Decision</button></div>
        </form>
      )}

      {current.hr_decision && <p className="muted">HR decision: <strong>{current.hr_decision.replace('_', ' ')}</strong> - {current.hr_decision_comments}</p>}
    </section>
  );
}
